"use client";

import { useCallback } from "react";
import { CustomScroll } from "@/components/CustomScroll";
import { CardsLoader, ListLoader } from "@/components/Loaders/ResultLoaders";
import { useIsMobile } from "@/features/layout/useIsMobile";
import type { Item } from "@/types";
import { cn } from "@/lib/cn";
import { useItems } from "../hooks/useItems";
import { usePOIView } from "../hooks/usePOIView";
import { useSelectedItemId } from "../hooks/useSelectedItemId";
import { useListViewType } from "../useListViewType";
import { ResultViews } from "./ResultViews";

/**
 * The scrolling body of the results panel.
 *
 * Cards are a desktop affordance: the mobile panel is always a list, whatever
 * the tenant configured, because two cards across a phone leaves room for
 * neither the image nor the title.
 */
export function ResultList() {
  const isMobile = useIsMobile();
  const { viewType } = useListViewType();
  const { isPending } = useItems();
  const { setSelectedItemId } = useSelectedItemId();
  const { setPOIView } = usePOIView();

  const isCards = viewType === "cards" && !isMobile;

  const onItemClick = useCallback(
    (item: Item) => {
      setSelectedItemId(item.id);
      // On mobile the details are a whole view rather than a panel beside the
      // map, so selecting has to navigate to it.
      if (isMobile) {
        setPOIView(true);
      }
    },
    [setSelectedItemId, setPOIView, isMobile]
  );

  if (isPending) return isCards ? <CardsLoader /> : <ListLoader />;

  return (
    <CustomScroll className={cn("overflow-auto p-4", isMobile && "h-[60vh]")}>
      <ResultViews
        viewType={isCards ? "cards" : "list"}
        onItemClick={onItemClick}
      />
    </CustomScroll>
  );
}
